/// <amd-module name="Tfs/Frame/Components/Composite/Composite" />
import * as React from "react";
import * as Resources from "Tfs/Frame/Resources";

import { ObservableCollection } from "VSS/Core/Observable";
import { VssComponent } from "VSS/Platform/Layout";

import { ScreenContext, ScreenSize } from "VSS/Core/Util/Screen";
import { ExpandableContainer, IExpandable } from "VSSUI/Expandable";
import { Icon, IconSize } from "VSSUI/Icon";
import { ContextualMenu, IMenuItem, MenuItemType } from "VSSUI/Menu";
import { Location } from "VSSUI/Utilities/Position";
import { ScreenSizeConditional } from "VSSUI/Utilities/ScreenSize";

import { getContributedItems } from "VSS/Features/PlatformUI/ContributedItemUtil";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";

interface ICompositedMenu {
    title: string;
}

export class Composite extends VssComponent<{}> {
    private static readonly headerRanks: { [contributionId: string]: number } = {
        "ms.vss-tfs-web.market-menu": 20,
        "ms.vss-tfs-web.help-menu": 30
    };
    private static getMenuHeader(menu: { id: string; contribution: ICompositedMenu }, groupKey: string): IMenuItem | null {
        if (!menu.contribution.title) {
            return null;
        }
        return {
            id: `header_${menu.id}`,
            itemType: MenuItemType.Header,
            groupKey: groupKey,
            text: menu.contribution.title,
            rank: Composite.headerRanks[menu.id] || 50
        };
    }

    public render(): JSX.Element {
        return (
            <ScreenSizeConditional condition={screenSize => screenSize <= ScreenSize.xsmall}>
                <ExpandableContainer
                    ariaLabel={Resources.HelpAndActions}
                    className="commandbar-item commandbar-icon cursor-pointer flex-row flex-noshrink justify-center"
                    renderCallout={this.renderCallout}
                    role="menuitem"
                >
                    <Icon iconName="MoreVertical" size={IconSize.medium} />
                </ExpandableContainer>
            </ScreenSizeConditional>
        );
    }

    private renderCallout = (dropdown: IExpandable, id: string, element: HTMLElement): JSX.Element => {
        const contributedMenus = getContributedItems<ICompositedMenu>(
            this.context.pageContext,
            "ms.vss-tfs-web.vertical-header-composite",
            undefined,
            "ms.vss-web.menu"
        );

        const menuItems = new ObservableCollection<IMenuItem>();
        for (const menu of contributedMenus) {
            // Turn each menu into a sub-group
            const groupedItems = getMenuItems(this.context.pageContext, menu.id, undefined);

            // In the composite view, we don't separate the "service" and "getting-started" groups
            for (const item of groupedItems.value) {
                if (item.groupKey === "service" || item.groupKey === "getting-started") {
                    item.groupKey = "help";
                }
            }

            const firstItemGroupKey = groupedItems.value[0] && groupedItems.value[0].groupKey;
            const header = Composite.getMenuHeader(menu, firstItemGroupKey || menu.id);
            if (header) {
                menuItems.push([header]);
            }

            menuItems.push(groupedItems);
        }

        return (
            <ContextualMenu
                onDismiss={dropdown.collapse}
                anchorElement={element}
                anchorOrigin={{ horizontal: Location.end, vertical: Location.end }}
                menuProps={{
                    id: "composite-menu",
                    items: menuItems
                }}
                menuOrigin={{ horizontal: Location.end, vertical: Location.start }}
            />
        );
    };
}

VssComponent.register("ms.vss-tfs-web.composite", Composite);
